import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, isSameMonth, isSameDay, format, getDay,
} from "date-fns";
import { CalendarDays, Plus, ChevronLeft, ChevronRight, Check, Plane, Save, Pencil, CalendarRange, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DeleteButton } from "@/components/DeleteButton";

const iso = (d) => format(d, "yyyy-MM-dd");
const first = (name) => (name || "").split(" ")[0];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Schedule() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cursor, setCursor] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [rates, setRates] = useState({});
  const [assignOpen, setAssignOpen] = useState(false);
  const EMPTY = { worker_id: "", date: "", start_time: "09:00", end_time: "17:00", note: "" };
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const WD_OPTS = [{ l: "Mon", v: 1 }, { l: "Tue", v: 2 }, { l: "Wed", v: 3 }, { l: "Thu", v: 4 }, { l: "Fri", v: 5 }, { l: "Sat", v: 6 }, { l: "Sun", v: 0 }];
  const BULK_EMPTY = { worker_id: "", weekdays: { 1: true, 2: true, 3: true, 4: true, 5: true, 6: false, 0: false }, start_time: "09:00", end_time: "17:00", note: "" };
  const [bulk, setBulk] = useState(BULK_EMPTY);
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  const BULK_RM_EMPTY = { worker_id: "", weekdays: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 0: false } };
  const [bulkRemove, setBulkRemove] = useState(BULK_RM_EMPTY);

  const AV_DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
  const avEmpty = () => Object.fromEntries(AV_DAYS.map(([k]) => [k, { available: false, start_time: "09:00", end_time: "17:00" }]));
  const [avail, setAvail] = useState(avEmpty());
  const [savingAvail, setSavingAvail] = useState(false);
  const [allAvail, setAllAvail] = useState([]);

  const load = useCallback(async () => {
    try {
      if (isAdmin) {
        const [s, w, t, av] = await Promise.all([api.get("/staff/shifts"), api.get("/staff/workers"), api.get("/staff/time-off"), api.get("/staff/availability")]);
        setShifts(s.data); setWorkers(w.data); setTimeOff(t.data.filter((r) => r.status === "approved"));
        setRates(Object.fromEntries(w.data.map((x) => [x.id, x.hourly_rate ?? 0])));
        setAllAvail(av.data);
      } else {
        const [s, t, av] = await Promise.all([api.get("/staff/shifts/my"), api.get("/staff/time-off/my"), api.get("/staff/availability/my")]);
        setShifts(s.data); setTimeOff(t.data.filter((r) => r.status === "approved"));
        const base = avEmpty();
        Object.entries(av.data || {}).forEach(([k, v]) => { if (base[k]) base[k] = { available: !!v.available, start_time: v.start_time || "09:00", end_time: v.end_time || "17:00" }; });
        setAvail(base);
      }
    } catch { /* */ }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const setDay = (k, field, value) => setAvail((a) => ({ ...a, [k]: { ...a[k], [field]: value } }));
  async function saveAvailability() {
    setSavingAvail(true);
    try { await api.put("/staff/availability", { days: avail }); toast.success("Preferred availability saved. Your manager can see it when scheduling."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save."); }
    finally { setSavingAvail(false); }
  }

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const days = eachDayOfInterval({ start: startOfWeek(monthStart, { weekStartsOn: 1 }), end: endOfWeek(monthEnd, { weekStartsOn: 1 }) });
  const shiftsOn = (d) => shifts.filter((s) => s.date === iso(d));
  const offOn = (d) => timeOff.filter((r) => iso(d) >= r.start_date && iso(d) <= r.end_date);
  const monthShifts = shifts.filter((s) => s.date >= iso(monthStart) && s.date <= iso(monthEnd));
  const unconfirmedCount = monthShifts.filter((s) => !s.confirmed).length;

  function openAssign(day) {
    if (!isAdmin) return;
    setEditId(null);
    setForm({ ...EMPTY, date: iso(day) });
    setAssignOpen(true);
  }

  function openEdit(s) {
    if (!isAdmin) return;
    setEditId(s.id);
    setForm({ worker_id: s.worker_id, date: s.date, start_time: s.start_time, end_time: s.end_time, note: s.note || "" });
    setAssignOpen(true);
  }

  async function submitAssign(e) {
    e.preventDefault();
    if (!form.worker_id || !form.date || !form.start_time || !form.end_time) return toast.error("Pick a worker, date and times.");
    try {
      if (editId) { await api.patch(`/staff/shifts/${editId}`, form); toast.success("Shift updated."); }
      else { await api.post("/staff/shifts", form); toast.success("Shift assigned."); }
      setAssignOpen(false); setEditId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function submitBulk(e) {
    e.preventDefault();
    if (!bulk.worker_id) return toast.error("Pick a worker.");
    const selected = WD_OPTS.filter((o) => bulk.weekdays[o.v]).map((o) => o.v);
    if (selected.length === 0) return toast.error("Pick at least one weekday.");
    const dates = eachDayOfInterval({ start: monthStart, end: monthEnd })
      .filter((d) => selected.includes(getDay(d))).map((d) => iso(d));
    if (dates.length === 0) return toast.error("No matching days this month.");
    try {
      const { data } = await api.post("/staff/shifts/bulk", {
        worker_id: bulk.worker_id, dates, start_time: bulk.start_time, end_time: bulk.end_time, note: bulk.note,
      });
      toast.success(`${data.created} shift(s) added${data.skipped ? `, ${data.skipped} skipped (already scheduled)` : ""}.`);
      setBulkOpen(false); setBulk(BULK_EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function submitBulkRemove(e) {
    e.preventDefault();
    if (!bulkRemove.worker_id) return toast.error("Pick a worker.");
    const selected = WD_OPTS.filter((o) => bulkRemove.weekdays[o.v]).map((o) => o.v);
    if (selected.length === 0) return toast.error("Pick at least one weekday.");
    const dates = eachDayOfInterval({ start: monthStart, end: monthEnd })
      .filter((d) => selected.includes(getDay(d))).map((d) => iso(d));
    if (dates.length === 0) return toast.error("No matching days this month.");
    try {
      const { data } = await api.post("/staff/shifts/bulk-remove", { worker_id: bulkRemove.worker_id, dates });
      toast.success(data.deleted ? `${data.deleted} shift(s) removed.` : "No matching shifts to remove.");
      setBulkRemoveOpen(false); setBulkRemove(BULK_RM_EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function confirmMonth() {
    try {
      const { data } = await api.post("/staff/shifts/confirm-range", { start: iso(monthStart), end: iso(monthEnd) });
      toast.success(data.confirmed ? `Confirmed ${data.confirmed} shift(s) for ${format(cursor, "MMMM")}.` : "No shifts to confirm this month.");
      load();
    } catch { toast.error("Failed to confirm."); }
  }

  async function saveRate(w) {
    try {
      await api.patch(`/staff/workers/${w.id}`, { hourly_rate: Number(rates[w.id] || 0) });
      toast.success(`${first(w.name)}'s rate saved.`);
    } catch { toast.error("Failed to save rate."); }
  }

  async function delShift(id) {
    try { await api.delete(`/staff/shifts/${id}`); toast.success("Shift removed."); load(); }
    catch { toast.error("Failed."); }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><CalendarDays className="h-6 w-6" /></div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Schedule</h1>
            <p className="text-sm text-muted-foreground">{isAdmin ? "Click any day to assign a worker. Approved time off shows automatically." : "Your monthly shifts. Confirm the month once your schedule looks right."}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <>
              <Button variant="outline" className="rounded-full" onClick={() => { setBulk(BULK_EMPTY); setBulkOpen(true); }} data-testid="shift-bulk-open"><CalendarRange className="mr-1 h-4 w-4" /> Bulk Assign</Button>
              <Button variant="outline" className="rounded-full text-red-600 hover:text-red-600" onClick={() => { setBulkRemove(BULK_RM_EMPTY); setBulkRemoveOpen(true); }} data-testid="shift-bulk-remove-open"><Trash2 className="mr-1 h-4 w-4" /> Bulk Remove</Button>
              <Button className="rounded-full" onClick={() => openAssign(new Date())} data-testid="shift-assign-open"><Plus className="mr-1 h-4 w-4" /> Assign Shift</Button>
            </>
          ) : (
            <Button className="rounded-full" onClick={confirmMonth} disabled={unconfirmedCount === 0} data-testid="confirm-month">
              <Check className="mr-1 h-4 w-4" /> {unconfirmedCount === 0 ? "Month Confirmed" : `Confirm ${format(cursor, "MMMM")}`}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCursor(subMonths(cursor, 1))} data-testid="sched-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <p className="min-w-[180px] text-center font-heading text-lg font-bold" data-testid="sched-month">{format(cursor, "MMMM yyyy")}</p>
        <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="sched-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-border/60 bg-card">
        <div className="grid grid-cols-7 border-b border-border/60 bg-secondary/40 text-center text-xs font-semibold text-muted-foreground">
          {WEEKDAYS.map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const inMonth = isSameMonth(day, cursor);
            const today = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} onClick={() => openAssign(day)}
                className={`min-h-[92px] border-b border-r border-border/40 p-1.5 ${inMonth ? "" : "bg-secondary/20 opacity-60"} ${isAdmin ? "cursor-pointer hover:bg-secondary/40" : ""}`}
                data-testid={`day-${iso(day)}`}>
                <div className={`mb-1 text-right text-xs ${today ? "font-bold text-primary" : "text-muted-foreground"}`}>{format(day, "d")}</div>
                <div className="space-y-1">
                  {shiftsOn(day).map((s) => (
                    <div key={s.id} title={s.note}
                      onClick={isAdmin ? (e) => { e.stopPropagation(); openEdit(s); } : undefined}
                      className={`truncate rounded px-1.5 py-0.5 text-[11px] font-medium ${isAdmin ? "hover:ring-1 hover:ring-primary/40" : ""} ${s.confirmed ? "bg-primary/15 text-primary" : "bg-orange-500/15 text-orange-700"}`}
                      data-testid={`cal-shift-${s.id}`}>
                      <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${s.confirmed ? "bg-primary" : "bg-orange-500"}`} />
                      {isAdmin ? first(s.worker_name) : "You"} {s.start_time}
                    </div>
                  ))}
                  {offOn(day).map((r) => (
                    <div key={r.id} className="truncate rounded bg-blue-500/10 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">
                      <Plane className="mr-0.5 inline h-2.5 w-2.5" />{isAdmin ? first(r.worker_name) : "Off"}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isAdmin ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-border/60 bg-card p-5">
            <h2 className="font-heading font-bold">Hourly pay rates</h2>
            <p className="mb-3 text-xs text-muted-foreground">Used to calculate pay on the Pay Preview.</p>
            <div className="space-y-2">
              {workers.length === 0 ? <p className="text-sm text-muted-foreground">No workers yet.</p>
                : workers.map((w) => (
                  <div key={w.id} className="flex items-center gap-2" data-testid={`rate-${w.id}`}>
                    <span className="flex-1 truncate text-sm font-medium">{w.name}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input type="number" step="0.01" min="0" className="h-9 w-24" value={rates[w.id] ?? 0}
                        onChange={(e) => setRates((r) => ({ ...r, [w.id]: e.target.value }))} data-testid={`rate-input-${w.id}`} />
                      <span className="text-xs text-muted-foreground">/hr</span>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => saveRate(w)} data-testid={`rate-save-${w.id}`}><Save className="h-3 w-3" /></Button>
                  </div>
                ))}
            </div>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-5">
            <h2 className="font-heading font-bold">{format(cursor, "MMMM")} shifts</h2>
            <div className="mt-3 space-y-2">
              {monthShifts.length === 0 ? <p className="text-sm text-muted-foreground">No shifts this month.</p>
                : monthShifts.sort((a, b) => a.date.localeCompare(b.date)).map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-border/50 p-2.5" data-testid={`sched-shift-${s.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{s.worker_name} <span className="text-muted-foreground">· {s.date} {s.start_time}–{s.end_time}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={s.confirmed ? "bg-primary" : "bg-orange-500"}>{s.confirmed ? "Confirmed" : "Awaiting"}</Badge>
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => openEdit(s)} data-testid={`shift-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <DeleteButton onConfirm={() => delShift(s.id)} label="this shift" testId={`shift-delete-${s.id}`} />
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-5 lg:col-span-2">
            <h2 className="font-heading font-bold">Worker preferred availability</h2>
            <p className="mb-3 text-xs text-muted-foreground">What each worker prefers for Monday–Sunday. Use it as a guide when assigning shifts.</p>
            <div className="space-y-3">
              {allAvail.length === 0 ? <p className="text-sm text-muted-foreground">No workers have set their availability yet.</p>
                : allAvail.map((a) => (
                  <div key={a.user_id} className="rounded-lg border border-border/50 p-3" data-testid={`admin-avail-${a.user_id}`}>
                    <p className="mb-1.5 text-sm font-semibold">{a.worker_name || "Worker"}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {AV_DAYS.map(([k, label]) => {
                        const d = (a.days || {})[k];
                        const on = d && d.available;
                        return (
                          <span key={k} className={`rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted/40 text-muted-foreground"}`}>
                            {label.slice(0, 3)}: {on ? `${d.start_time}–${d.end_time}` : "—"}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="rounded-xl border border-border/60 bg-card p-5">
            <h2 className="font-heading font-bold">My preferred availability</h2>
            <p className="mb-3 text-xs text-muted-foreground">Set the times you'd prefer to work each day, Monday to Sunday. Your manager sees this when building the schedule — it's a preference, not a guaranteed shift.</p>
            <div className="space-y-2">
              {AV_DAYS.map(([k, label]) => (
                <div key={k} className="flex flex-wrap items-center gap-3 rounded-lg border border-border/50 p-2.5" data-testid={`avail-row-${k}`}>
                  <label className="flex w-28 cursor-pointer items-center gap-2 text-sm font-medium">
                    <Checkbox checked={avail[k].available} onCheckedChange={(c) => setDay(k, "available", !!c)} data-testid={`avail-toggle-${k}`} />
                    {label}
                  </label>
                  {avail[k].available ? (
                    <div className="flex items-center gap-2">
                      <Input type="time" className="h-9 w-32" value={avail[k].start_time} onChange={(e) => setDay(k, "start_time", e.target.value)} data-testid={`avail-start-${k}`} />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input type="time" className="h-9 w-32" value={avail[k].end_time} onChange={(e) => setDay(k, "end_time", e.target.value)} data-testid={`avail-end-${k}`} />
                    </div>
                  ) : <span className="text-sm text-muted-foreground">Not available</span>}
                </div>
              ))}
            </div>
            <Button className="mt-4 rounded-full" onClick={saveAvailability} disabled={savingAvail} data-testid="avail-save"><Save className="mr-1 h-4 w-4" /> {savingAvail ? "Saving..." : "Save availability"}</Button>
          </div>

          <div className="rounded-xl border border-border/60 bg-card p-5">
            <h2 className="font-heading font-bold">{format(cursor, "MMMM")} shifts</h2>
            <div className="mt-3 space-y-2">
              {monthShifts.length === 0 ? <p className="text-sm text-muted-foreground">No shifts scheduled this month.</p>
                : monthShifts.sort((a, b) => a.date.localeCompare(b.date)).map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-border/50 p-2.5" data-testid={`my-shift-${s.id}`}>
                    <p className="text-sm font-medium">{s.date} · {s.start_time}–{s.end_time}{s.note ? ` · ${s.note}` : ""}</p>
                    <Badge className={s.confirmed ? "bg-primary" : "bg-orange-500"}>{s.confirmed ? "Confirmed" : "Awaiting"}</Badge>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit Shift" : "Assign Shift"}</DialogTitle>
            <DialogDescription>Pick a worker, date and times. They'll see it on their schedule to confirm.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAssign} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Worker</Label>
              <Select value={form.worker_id} onValueChange={(v) => setForm({ ...form, worker_id: v })}>
                <SelectTrigger data-testid="assign-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>
                  {workers.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No workers — add them under Staff.</div>
                    : workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" data-testid="assign-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Start</Label><Input type="time" data-testid="assign-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label className="text-xs">End</Label><Input type="time" data-testid="assign-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Note</Label><Input data-testid="assign-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <Button type="submit" className="w-full rounded-full" data-testid="assign-submit"><Plus className="mr-1 h-4 w-4" /> {editId ? "Save Changes" : "Assign"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Bulk Assign — {format(cursor, "MMMM yyyy")}</DialogTitle>
            <DialogDescription>Assign a worker to every selected weekday across this month. Days they're already scheduled are skipped.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBulk} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Worker</Label>
              <Select value={bulk.worker_id} onValueChange={(v) => setBulk({ ...bulk, worker_id: v })}>
                <SelectTrigger data-testid="bulk-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>
                  {workers.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No workers — add them under Staff.</div>
                    : workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Weekdays</Label>
              <div className="flex flex-wrap gap-2">
                {WD_OPTS.map((o) => (
                  <label key={o.v} className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-sm" data-testid={`bulk-wd-${o.v}`}>
                    <Checkbox checked={!!bulk.weekdays[o.v]} onCheckedChange={(c) => setBulk((b) => ({ ...b, weekdays: { ...b.weekdays, [o.v]: !!c } }))} />
                    {o.l}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Start</Label><Input type="time" data-testid="bulk-start" value={bulk.start_time} onChange={(e) => setBulk({ ...bulk, start_time: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label className="text-xs">End</Label><Input type="time" data-testid="bulk-end" value={bulk.end_time} onChange={(e) => setBulk({ ...bulk, end_time: e.target.value })} required /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Note</Label><Input data-testid="bulk-note" value={bulk.note} onChange={(e) => setBulk({ ...bulk, note: e.target.value })} /></div>
            <Button type="submit" className="w-full rounded-full" data-testid="bulk-submit"><CalendarRange className="mr-1 h-4 w-4" /> Assign Across {format(cursor, "MMMM")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkRemoveOpen} onOpenChange={setBulkRemoveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Bulk Remove — {format(cursor, "MMMM yyyy")}</DialogTitle>
            <DialogDescription>Remove a worker's shifts on every selected weekday across this month. Use this to undo a bulk-assign mistake. This can't be undone.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBulkRemove} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Worker</Label>
              <Select value={bulkRemove.worker_id} onValueChange={(v) => setBulkRemove({ ...bulkRemove, worker_id: v })}>
                <SelectTrigger data-testid="bulk-remove-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>
                  {workers.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No workers — add them under Staff.</div>
                    : workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Weekdays</Label>
              <div className="flex flex-wrap gap-2">
                {WD_OPTS.map((o) => (
                  <label key={o.v} className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-sm" data-testid={`bulk-remove-wd-${o.v}`}>
                    <Checkbox checked={!!bulkRemove.weekdays[o.v]} onCheckedChange={(c) => setBulkRemove((b) => ({ ...b, weekdays: { ...b.weekdays, [o.v]: !!c } }))} />
                    {o.l}
                  </label>
                ))}
              </div>
            </div>
            <Button type="submit" variant="destructive" className="w-full rounded-full" data-testid="bulk-remove-submit"><Trash2 className="mr-1 h-4 w-4" /> Remove Across {format(cursor, "MMMM")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
